import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import ConfirmDialog from "@/components/ConfirmDialog";

const CourseCard = ({ c, isAdmin, onEdit, onDelete }) => (
  <Card className="overflow-hidden bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/60 transition-all flex flex-col" data-testid={`course-card-${c.course_id}`}>
    <div className="aspect-[16/9] bg-gradient-to-br from-purple-900/40 to-pink-900/40 relative">
      {c.image ? (
        <img src={c.image} alt={c.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-5xl">🎓</div>
      )}
      {c.price_label && (
        <span className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full border ${
          c.is_free
            ? "bg-emerald-500/30 text-emerald-100 border-emerald-400/40"
            : "bg-purple-500/30 text-purple-100 border-purple-400/40"
        }`}>{c.price_label}</span>
      )}
    </div>
    <div className="p-4 flex-1 flex flex-col gap-2">
      <h3 className="text-white font-bold leading-tight">{c.title}</h3>
      {c.description && <p className="text-sm text-gray-400 line-clamp-3">{c.description}</p>}
      <div className="mt-auto pt-2 flex gap-2 items-center">
        {c.link && (
          <a href={c.link} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid={`course-open-${c.course_id}`}>
            <Button className="w-full bg-purple-600 hover:bg-purple-500 text-white">View course</Button>
          </a>
        )}
        {isAdmin && (
          <>
            <Button onClick={() => onEdit(c)} variant="outline" className="text-white border-gray-700" data-testid={`course-edit-${c.course_id}`}>Edit</Button>
            <Button onClick={() => onDelete(c)} variant="outline" className="text-red-300 border-red-500/40 hover:bg-red-500/20" data-testid={`course-delete-${c.course_id}`}>Delete</Button>
          </>
        )}
      </div>
    </div>
  </Card>
);

const CourseEditor = ({ open, onClose, onSaved, existing }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [link, setLink] = useState("");
  const [priceLabel, setPriceLabel] = useState("");
  const [isFree, setIsFree] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(existing?.title || "");
    setDescription(existing?.description || "");
    setImage(existing?.image || "");
    setLink(existing?.link || "");
    setPriceLabel(existing?.price_label || "");
    setIsFree(!!existing?.is_free);
  }, [open, existing]);

  const submit = async () => {
    if (title.trim().length < 2) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        image: image.trim() || null,
        link: link.trim() || null,
        price_label: priceLabel.trim() || null,
        is_free: isFree,
      };
      let res;
      if (existing) {
        res = await api.patch(`/courses/${existing.course_id}`, body);
      } else {
        res = await api.post("/courses", body);
      }
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="course-editor-dialog">
      <div className="w-full max-w-lg bg-[#161620] border border-purple-500/40 rounded-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">{existing ? "Edit course" : "New course"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl" aria-label="Close">×</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-black/40 text-white border-gray-700" data-testid="course-title-input" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="bg-black/40 text-white border-gray-700" data-testid="course-desc-input" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Image URL</label>
            <Input value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://…" className="bg-black/40 text-white border-gray-700" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Link</label>
            <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" className="bg-black/40 text-white border-gray-700" data-testid="course-link-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400">Price label</label>
              <Input value={priceLabel} onChange={(e) => setPriceLabel(e.target.value)} placeholder="$49 or Free" className="bg-black/40 text-white border-gray-700" />
            </div>
            <label className="flex items-center gap-2 mt-6 text-sm text-gray-300">
              <input type="checkbox" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} data-testid="course-free-checkbox" />
              Mark as free
            </label>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" className="text-white border-gray-700">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="course-save-button">
            {saving ? "Saving…" : (existing ? "Save" : "Create")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CoursesPage = () => {
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => { api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(null)); }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const r = await api.get("/courses");
      setCourses(r.data?.courses || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load courses");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchCourses(); }, []);

  const isAdmin = user?.role === "admin";

  const onEdit = (c) => { setEditing(c); setShowEditor(true); };
  const onDelete = (c) => setPendingDelete(c);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/courses/${pendingDelete.course_id}`);
      toast.success("Deleted");
      fetchCourses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="courses-page">
      <SharedNavigation currentUser={user} activePage="courses" />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
              <span>🎓</span> Courses & Workshops
            </h1>
            <p className="text-gray-400 mt-1 text-sm">
              Curated learning to grow your sound, brand and business.
            </p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => { setEditing(null); setShowEditor(true); }}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold"
              data-testid="course-create-button"
            >
              + New course
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="courses-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-12" data-testid="courses-empty">
            <p className="text-5xl mb-3">🎓</p>
            <p className="text-white text-lg font-semibold">No courses yet</p>
            <p className="text-gray-400 text-sm mt-1">Check back soon — admins are curating the first drop.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="courses-grid">
            {courses.map((c) => (
              <CourseCard key={c.course_id} c={c} isAdmin={isAdmin} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </div>
        )}
      </div>

      <CourseEditor
        open={showEditor}
        onClose={() => { setShowEditor(false); setEditing(null); }}
        onSaved={() => { setShowEditor(false); setEditing(null); fetchCourses(); toast.success("Saved"); }}
        existing={editing}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => { if (!o) setPendingDelete(null); }}
        title={pendingDelete ? `Delete "${pendingDelete.title}"?` : "Delete course?"}
        description="This permanently removes the course. This cannot be undone."
        confirmText="Delete"
        destructive
        onConfirm={confirmDelete}
        testId="course-delete-confirm"
      />
    </div>
  );
};

export default CoursesPage;
